<template>
  <div v-if="!loaderActiveForLogout" :style="styleObject" class="page-header align-items-start min-vh-100" tabindex="-1">
    <span class="mask bg-white"></span>

    <!-- MTH002/005/006/007: reskinned login shell (theme2) — shared hero, card per state -->
    <div v-if="isThemedLoginScreen" class="mlx-page" :style="{ '--mlx-accent': themeColorTheme2 }">
      <div class="mlx-hero">
        <swiper
          v-if="slidesLandingPage.length"
          class="mlx-swiper"
          :slides-per-view="1"
          :space-between="0"
          :loop="slidesLandingPage.length > 1"
          :autoplay="{ delay: 3000, disableOnInteraction: false }"
          :pagination="{ clickable: true, el: '.mlx-pagination' }"
        >
          <swiper-slide v-for="(value, index) in slidesLandingPage" :key="index">
            <img class="mlx-hero-img" :src="value.file_url" alt="" />
          </swiper-slide>
        </swiper>
        <div
          v-else
          class="mlx-hero-fallback"
          :style="`background: linear-gradient(160deg, ${themeColorTheme2} 0%, #201a1a 100%);`"
        ></div>
        <div class="mlx-hero-overlay"></div>
        <div class="mlx-brand">
          <div class="mlx-brand-logo"><img :src="loginLogo" alt="Logo" /></div>
          <span v-if="brandName" class="mlx-brand-name">{{ brandName }}</span>
        </div>

        <!-- MTH069: language selector — same pill + dropdown as the menu top bar,
             so a customer can pick their language before entering the flow. -->
        <div class="mlx-lang-wrap">
          <div class="mlx-lang" role="group" aria-label="Language">
            <button
              type="button"
              class="mlx-lang-seg"
              :style="{ background: themeColorTheme2 }"
              :aria-label="`Switch language (currently ${activeLabel})`"
              @click="toggleLang"
            >
              <span class="mlx-lang-text">{{ activeLabel }}</span>
            </button>
            <button
              type="button"
              class="mlx-lang-caret"
              aria-haspopup="listbox"
              :aria-expanded="langOpen ? 'true' : 'false'"
              aria-label="Change language"
              @click="toggleLangMenu"
            >
              <i class="material-icons-round" aria-hidden="true">expand_more</i>
            </button>
          </div>

          <template v-if="langOpen">
            <div class="mlx-lang-backdrop" @click="langOpen = false"></div>
            <ul class="mlx-lang-menu" role="listbox">
              <li
                v-for="opt in langOptions"
                :key="opt.code"
                class="mlx-lang-option"
                :class="{ 'is-active': opt.code === activeLang }"
                :style="opt.code === activeLang ? { background: themeColorTheme2 } : null"
                role="option"
                :aria-selected="opt.code === activeLang ? 'true' : 'false'"
                @click="selectLang(opt)"
              >
                {{ opt.label }}
              </li>
            </ul>
          </template>
        </div>

        <div class="mlx-pagination"></div>
      </div>

      <div v-if="isLoginNormal" class="mlx-card">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <div class="mlx-field">
          <label class="mlx-label">{{ $t('signIn.mobileNumber') }}</label>
          <div class="mlx-input-box">
            <div class="mlx-code">
              <select
                v-if="checkAllowForeignerPhoneNumber == 'true' || checkAllowForeignerPhoneNumber == true"
                id="country_id"
                class="mlx-select"
                v-model="form1.phone_code"
              >
                <option v-for="(value, key) in countryData" :key="key" :value="value.value">
                  {{ value.label }}
                </option>
              </select>
              <span v-else class="mlx-code-fixed">+{{ form1.phone_code }}</span>
            </div>
            <input
              id="phone_number"
              class="mlx-input"
              type="tel"
              inputmode="numeric"
              placeholder="8123 4567"
              v-model="form1.phone_number"
              name="phone_number"
              @change="validatePhoneNumber($event)"
            />
          </div>
        </div>
        <button class="mlx-btn-primary" @click="validatePhone()" :style="`background: ${buttonColorTheme2};`">
          {{ $t('signIn.start') }}
        </button>
        <div class="mlx-or">
          <span class="mlx-line"></span>
          <span class="mlx-or-text">{{ $t('signIn.or') }}</span>
          <span class="mlx-line"></span>
        </div>
        <button
          v-if="branch_code != ''"
          class="mlx-btn-guest"
          @click="continueAsGuest()"
          :style="`border-color: ${themeColorTheme2}; color: ${themeColorTheme2};`"
        >
          {{ $t('signIn.continueAsGuest') }}
        </button>
        <!-- The two links sit inside the sentence, so the whole notice is one
             translatable message with named slots rather than glued fragments. -->
        <i18n-t keypath="signIn.agreeNotice" tag="p" class="mlx-footer" scope="global">
          <template #terms><a @click="openTerms()">{{ $t('signIn.termsOfService') }}</a></template>
          <template #privacy><a @click="openTerms()">{{ $t('signIn.privacyPolicy') }}</a></template>
        </i18n-t>
      </div>

      <!-- MTH005: passcode entry card -->
      <div v-if="isPasscodeEntry" class="mlx-card mlx-card-center">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <h1 class="mlx-title">{{ $t('signIn.enterPasscodeTitle') }}</h1>
        <p class="mlx-subtitle">{{ $t('signIn.enterPasscodeSubtitle') }}</p>
        <div class="mlx-otp-wrap">
          <v-otp-input
            ref="inputEnterPasscode"
            input-classes="mlx-otp-box"
            separator=""
            :num-inputs="4"
            :should-auto-focus="true"
            :is-input-num="true"
            :conditionalClass="['one', 'two', 'three', 'four']"
            :placeholder="['', '', '', '']"
            @on-change="handleOnChange"
            @on-complete="handleOnCompleteAlreadyPasscode"
            v-model="form2.passcode"
          />
        </div>
        <button
          class="mlx-btn-primary mlx-btn-pill"
          @click="handleOnCompleteAlreadyPasscode(form2.passcode)"
          :style="`background: ${buttonColorTheme2};`"
        >
          {{ $t('signIn.verify') }}
        </button>
        <p class="mlx-resend">
          {{ $t('signIn.forgotPasscode') }}
          <a @click="handleEnbaleOptionForgotPasscode()">{{ $t('signIn.reset') }}</a>
        </p>
      </div>

      <!-- MTH006: OTP entry card -->
      <div v-if="isOtp" class="mlx-card mlx-card-center">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <h1 class="mlx-title">{{ $t('signIn.verificationTitle') }}</h1>
        <p class="mlx-subtitle">
          {{ $t('signIn.verificationSubtitle') }}
          <span class="mlx-strong">+{{ form1.phone_code }} {{ form2.phone_number || form1.phone_number }}</span>
        </p>
        <div class="mlx-otp-wrap">
          <v-otp-input
            ref="inputOTP"
            input-classes="mlx-otp-box"
            separator=""
            :num-inputs="4"
            :should-auto-focus="true"
            :is-input-num="true"
            :conditionalClass="['one', 'two', 'three', 'four']"
            :placeholder="['', '', '', '']"
            @on-change="handleOnChange"
            @on-complete="handleOnComplete"
            v-model="form2.otp"
          />
        </div>
        <button
          class="mlx-btn-primary mlx-btn-pill"
          @click="handleOnComplete(form2.otp)"
          :style="`background: ${buttonColorTheme2};`"
        >
          {{ $t('signIn.verify') }}
        </button>
        <div class="mlx-countdown" v-html="countDownText"></div>
        <p class="mlx-resend">
          {{ $t('signIn.didntReceiveCode') }}
          <a @click="sendOTP(true)">{{ $t('signIn.resendCode') }}</a>
        </p>
      </div>

      <!-- MTH007: forget-passcode — single reset-to-birthday button -->
      <div v-if="isForgetChoice" class="mlx-card mlx-card-center">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <template v-if="!isPasscodeReset">
          <h1 class="mlx-title">{{ $t('signIn.resetPasscodeTitle') }}</h1>
          <p class="mlx-subtitle">
            {{ $t('signIn.resetPasscodeSubtitle') }}
          </p>
          <button
            class="mlx-btn-primary mlx-btn-pill"
            @click="resetPasscodeToBirthday()"
            :style="`background: ${buttonColorTheme2};`"
          >
            {{ $t('signIn.resetPasswordButton') }}
          </button>
          <p class="mlx-hint">{{ $t('signIn.ddmmHint') }}</p>
        </template>
        <template v-else>
          <h1 class="mlx-title">{{ $t('signIn.passcodeResetTitle') }}</h1>
          <p class="mlx-subtitle">
            {{ $t('signIn.passcodeResetSubtitle') }}
          </p>
          <button
            class="mlx-btn-primary mlx-btn-pill"
            @click="backToPasscodeEntry()"
            :style="`background: ${buttonColorTheme2};`"
          >
            {{ $t('signIn.continueToLogin') }}
          </button>
        </template>
      </div>

      <!-- MTH007: enter birthday (DDMM) card -->
      <div v-if="isBirthdayEntry" class="mlx-card mlx-card-center">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <h1 class="mlx-title">{{ $t('signIn.enterBirthdayTitle') }}</h1>
        <p class="mlx-subtitle">{{ $t('signIn.enterBirthdaySubtitle') }}</p>
        <div class="mlx-otp-wrap">
          <v-otp-input
            ref="inputOTP"
            input-classes="mlx-otp-box"
            separator=""
            :num-inputs="4"
            :should-auto-focus="true"
            :is-input-num="true"
            :conditionalClass="['one', 'two', 'three', 'four']"
            :placeholder="['', '', '', '']"
            @on-change="handleOnChange"
            @on-complete="handleOnCompleteAlreadyPasscode"
            v-model="form2.passcode"
          />
        </div>
        <p
          class="mlx-resend"
          v-if="!isEnableGetOneTimePasscode && (checkAllowResetPasscodeToOneTimeOtp == 'true' || checkAllowResetPasscodeToOneTimeOtp == true)"
        >
          {{ $t('signIn.forgotBirthday') }}
          <a @click="handleGetOneTimePasscode()">{{ $t('signIn.getOneTimePasscode') }}</a>
        </p>
        <p class="mlx-hint">{{ $t('signIn.ddmmHint') }}</p>
      </div>

      <!-- MTH007: enter one-time passcode card -->
      <div v-if="isOneTimeEntry" class="mlx-card mlx-card-center">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <h1 class="mlx-title">{{ $t('signIn.oneTimePasscodeTitle') }}</h1>
        <p class="mlx-subtitle">{{ $t('signIn.oneTimePasscodeSubtitle') }}</p>
        <div class="mlx-otp-wrap">
          <v-otp-input
            ref="inputOTP"
            input-classes="mlx-otp-box"
            separator=""
            :num-inputs="4"
            :should-auto-focus="true"
            :is-input-num="true"
            :conditionalClass="['one', 'two', 'three', 'four']"
            :placeholder="['', '', '', '']"
            @on-change="handleOnChange"
            @on-complete="handleOnCompleteAlreadyPasscode"
            v-model="form2.passcode"
          />
        </div>
      </div>

      <!-- MTH008: create-PIN card (Advocado-style) — ENTER PIN + CONFIRM PIN, no Create Account button -->
      <div v-if="isPasscode || isConfirmPasscode" class="mlx-card mlx-pin-card">
        <div class="notifications">
          <material-alert v-if="error" color="danger" icon="ni ni-like-2 ni-lg" dismissible>
            {{ error }}
          </material-alert>
        </div>
        <h1 class="mlx-title mlx-pin-heading">{{ $t('signIn.joinTitle') }}</h1>
        <!-- <div class="mlx-perks">
          <div class="mlx-perk">
            <span class="mlx-perk-icon material-icons-round">account_balance_wallet</span>
            <span class="mlx-perk-label">{{ $t('signIn.perkCashback') }}</span>
          </div>
          <div class="mlx-perk">
            <span class="mlx-perk-icon mlx-perk-icon-fill material-icons-round">card_giftcard</span>
            <span class="mlx-perk-label">{{ $t('signIn.perkWelcomeVoucher') }}</span>
          </div>
          <div class="mlx-perk">
            <span class="mlx-perk-icon mlx-perk-icon-fill material-icons-round">cake</span>
            <span class="mlx-perk-label">{{ $t('signIn.perkBirthdayVoucher') }}</span>
          </div>
        </div> -->
        <a class="mlx-pin-back" @click="backToPhoneEntry()">{{ $t('common.back') }}</a>
        <h2 class="mlx-pin-title">{{ $t('signIn.createPinTitle') }}</h2>
        <p class="mlx-pin-sub">
          {{ $t('signIn.createPinSubtitle') }}
        </p>

        <div v-if="isPasscode" class="mlx-pin-field">
          <label class="mlx-label">{{ $t('signIn.enterPin') }}</label>
          <div class="mlx-otp-wrap mlx-pin-otp">
            <v-otp-input
              ref="inputCreatePasscode"
              input-classes="mlx-otp-box"
              separator=""
              :num-inputs="4"
              :should-auto-focus="true"
              :is-input-num="true"
              :conditionalClass="['one', 'two', 'three', 'four']"
              :placeholder="['', '', '', '']"
              @on-change="handleOnChange"
              @on-complete="handleOnCompletePasscode"
              v-model="form2.passcode"
            />
          </div>
        </div>

        <div v-if="isConfirmPasscode" class="mlx-pin-field">
          <label class="mlx-label">{{ $t('signIn.confirmPin') }}</label>
          <div class="mlx-otp-wrap mlx-pin-otp">
            <v-otp-input
              ref="inputConfirmPasscode"
              input-classes="mlx-otp-box"
              separator=""
              :num-inputs="4"
              :should-auto-focus="false"
              :is-input-num="true"
              :conditionalClass="['one', 'two', 'three', 'four']"
              :placeholder="['', '', '', '']"
              @on-change="handleOnChange"
              @on-complete="handleOnCompleteConfirmPasscode"
              v-model="form2.confirmPasscode"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="position-fixed top-1 end-1 z-index-2" style="z-index: 10001 !important;">
      <material-snackbar v-if="snackbar === 'danger' || snackbar === 'success'" title="" date=""
                :description="message" :icon="{ component: 'campaign', color: 'white' }" :color="snackbar"
                :close-handler="closeSnackbar" />
    </div>
    <div v-if="loaderActive" class="mlx-loader-overlay" :style="{ '--mlx-accent': themeColorTheme2 }">
      <div class="mlx-loader-spinner"></div>
    </div>
    <TermService :phone="form1.phone_number" :agree="agree" :view-only="termsViewOnly" />
    <ModalSuccess :processAcction="closeModal" :success="false"
      :message="modalErrorMessage" buttonText="Ok" />
  </div>
</template>

<script>
import MaterialAlert from "@/components/MaterialAlert.vue";
import { mapMutations } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import VOtpInput from "vue3-otp-input";
import router from "@/router";
import TermService from "@/components/TermService.vue";
import AuthService from "../services/memberAuth.service";
import { useRoute } from 'vue-router'
import { ref } from "vue";
import $ from "jquery";
import TokenService from "../services/token.service";
import MemberService from "../services/member.service";
import CryptoJS from 'crypto-js';
import axios from "axios";
import { localStorageName } from '@/config/utils.js';
import {
  MENU_POPUP_SESSION_KEY,
  AFTER_LOGIN_ORDERING_PAGE,
  AFTER_LOGIN_MEMBER_PAGE,
  AFTER_LOGIN_REWARD_PAGE,
} from '@/config/const';
import ModalSuccess from "@/components/ModalSuccess.vue";
import defaultLogo from "@/assets/img/logo.png";
import OrderService from "../services/order.service";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Pagination, Autoplay } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import {
  LANGUAGE_PAIRS,
  readLanguagePair,
  setLanguagePair,
  setLocale,
  toggleLocale,
} from "@/i18n";

SwiperCore.use([Pagination, Autoplay]);

// MTH069: bilingual pairs, defined once in @/i18n so the login pill and the
// menu top bar can't drift apart.
const LANG_OPTIONS = LANGUAGE_PAIRS.map((p) => ({
  code: p.second,
  label: p.label,
}));
// Short single-language labels shown on the left toggle segment.
const LANG_LABEL = {
  en: "Eng",
  zh: "Chn",
  ja: "Jap",
  th: "Thai",
  ko: "Kor",
};

export default {
  name: "sign-in",
  components: {
    MaterialAlert,
    MaterialSnackbar,
    VOtpInput,
    TermService,
    ModalSuccess,
    Swiper,
    SwiperSlide
  },
  setup() {
    const inputOTP = ref(null);
    const inputEnterPasscode = ref(null);
    const inputCreatePasscode = ref(null);
    const inputConfirmPasscode = ref(null);

    const clearInputOTP = () => {
      inputOTP.value.clearInput();
    };
    const clearInputEnterPasscode = () => {
      inputEnterPasscode.value.clearInput();
    };
    const clearInputCreatePasscode = () => {
      inputCreatePasscode.value.clearInput();
    };
    const clearInputConfirmPasscode = () => {
      inputConfirmPasscode.value.clearInput();
    };
    return { inputOTP, inputEnterPasscode, inputCreatePasscode, inputConfirmPasscode, clearInputOTP, clearInputEnterPasscode, clearInputCreatePasscode, clearInputConfirmPasscode };
  },
  data() {
    return {
      phone_code: "65",
      form1: {
        phone_number: "",
        invite_code: "",
        i_agree: false,
        phone_code: "65"
      },
      message: "",
      modalErrorMessage: "",
      form2: {
        otp: "",
        phone_number: "",
        i_agree: true,
        passcode: "",
        confirmPasscode: "",
        is_empty_passcode: false,
        phone_code: ""
      },
      snackbar: null,
      error: "",
      isLoginNormal: true,
      isPasscode: false,
      isConfirmPasscode: false,
      isAlreadyPasscode: false,
      isOtp: false,
      timerEnabled: true,
      countDown: 120,
      countDownText: "120s",
      timeout: "",
      timer_on: 0,
      merchantCode: "",
      loaderActive: false,
      isFirstLogin: false,
      branch_code: "",
      isForgetPassword: false,
      isShowEnterPasscodeYourBirtday: false,
      isShowEnterOneTimePasscode: false,
      checkAllowResetPasscodeToBirthday: null,
      checkAllowResetPasscodeToOneTimeOtp: null,
      isEnableGetOneTimePasscode: true,
      loaderActiveForLogout: false,
      countryData: [],
      checkAllowForeignerPhoneNumber: null,
      onlyAllowExistingMemberMooLogin: false,
      slidesLandingPage: [],
      brandName: "",
      isPasscodeReset: false,
      // MTH069: language pill state — whether the dropdown is open, and the
      // second (non-English) language of the active pair, persisted by @/i18n.
      langOpen: false,
      activeLang: readLanguagePair(),
      // MTH0xx: whether the terms/PDPA modal is opened for consent (checkbox +
      // Decline/Continue) or just to read (footer links, before phone is
      // entered/validated) — see openTerms()/validatePhone().
      termsViewOnly: true
    };
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  beforeUnmount() {
    this.loaderActiveForLogout = false;
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  created() {
    const onlyAllowExistingMemberMooLogin = localStorage.getItem(localStorageName("ONLY_ALLOW_EXISTING_MEMBER_MOO_LOGIN"));
    this.onlyAllowExistingMemberMooLogin = onlyAllowExistingMemberMooLogin == 'true' || onlyAllowExistingMemberMooLogin == '1' ? true : false;

    this.getCountriesForDropdown();
    const token = TokenService.getLocalAccessToken();
    const isLogout = this.$route.query.isLogout ?? '';

    if(isLogout != "" && token) {
      this.loaderActiveForLogout = true;
      TokenService.removeUser();
      router.push({ name: 'Order', params: this.$route.params });
    }
  },
  mounted() {
    const isAllowResetPasscodeToBirthday = localStorage.getItem(localStorageName("ALLOW_RESET_PASSCODE_TO_BIRTHDAY"), 'false');
    const isAllowResetPasscodeToOneTimeOtp = localStorage.getItem(localStorageName("ALLOW_RESET_PASSCODE_TO_ONE_TIME_OTP"), 'false');
    const isAllowForeignerPhoneNumber = localStorage.getItem(localStorageName("ALLOW_FOREIGNER_PHONE_NUMBER"), 'false');

    this.checkAllowResetPasscodeToBirthday = isAllowResetPasscodeToBirthday;
    this.checkAllowResetPasscodeToOneTimeOtp = isAllowResetPasscodeToOneTimeOtp;
    this.checkAllowForeignerPhoneNumber = isAllowForeignerPhoneNumber;
    this.form1.invite_code =
      this.$router.currentRoute.value.params.invite_code ?? "";
    const code = this.$router.currentRoute.value.params.merchant_code ?? "";
    this.merchantCode = code ? `/${code}` : "";
    const token = TokenService.getLocalAccessToken();
    const branch_code = this.$route.query.branch_code ?? this.$route.params.branch_code ?? '';

    if(branch_code != "") {
        this.branch_code = branch_code;
    }
    if (token && this.merchantCode) {
      if(branch_code != "") {
        AuthService.checkTokenHasExpired().then(
          (response) => {
            if (response.data.result.isSuccess) {
              router.push({ name: 'Order', params: this.$route.params });
            }
          },
          (error) => {
            console.log(error);
          }
        )
      } else {
        router.push({ name: 'Order', params: this.$route.params });
      }
    }

    const route = useRoute()

    if (route.query.phone) {
      this.form1.phone_number = route.query.phone;
      this.termsViewOnly = false;
      $('.bottom_modal_terms').click();
    }

    this.brandName = localStorage.getItem(localStorageName("MERCHANT_BRAND_NAME")) || "";
    this.getLandingBanner();
  },
  computed: {
    styleObject() {
      return {
        "--label-color": this.themeColorTheme2,
      };
    },
    loginLogo() {
      return this.$store.state.logoTheme2
        ? this.$store.state.logoTheme2
        : defaultLogo;
    },
    themeColorTheme2() {
      return this.$store.state.mainThemeColorTheme2
        ? this.$store.state.mainThemeColorTheme2
        : this.$store.state.color;
    },
    buttonColorTheme2() {
      return this.$store.state.primaryButtonColorTheme2
        ? this.$store.state.primaryButtonColorTheme2
        : this.themeColorTheme2;
    },
    isPasscodeEntry() {
      return this.isAlreadyPasscode
        && !this.isForgetPassword
        && !this.isShowEnterPasscodeYourBirtday
        && !this.isShowEnterOneTimePasscode;
    },
    isForgetChoice() {
      return this.isAlreadyPasscode && this.isForgetPassword;
    },
    isBirthdayEntry() {
      return this.isAlreadyPasscode && this.isShowEnterPasscodeYourBirtday;
    },
    isOneTimeEntry() {
      return this.isAlreadyPasscode && this.isShowEnterOneTimePasscode;
    },
    langOptions() {
      return LANG_OPTIONS;
    },
    // The language currently displayed — always English or the pair's second
    // language. Read from vue-i18n so the pill follows the app-wide locale.
    activeLabel() {
      return LANG_LABEL[this.$i18n.locale] || LANG_LABEL.en;
    },
    isThemedLoginScreen() {
      return this.isLoginNormal
        || this.isPasscodeEntry
        || this.isOtp
        || this.isForgetChoice
        || this.isBirthdayEntry
        || this.isOneTimeEntry
        || this.isPasscode
        || this.isConfirmPasscode;
    },
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    toggleLangMenu() {
      this.langOpen = !this.langOpen;
    },
    // Left segment: flip between English and the active pair's second language.
    toggleLang() {
      this.langOpen = false;
      toggleLocale();
    },
    // Select a language pairing from the dropdown: persist the pair and display
    // its second language right away.
    selectLang(opt) {
      this.activeLang = setLanguagePair(opt.code);
      this.langOpen = false;
      setLocale(this.activeLang);
    },
    // MTH077: logout wipes localStorage (Profile.handleLogout) and App.vue does not
    // re-run on SPA navigation, so MERCHANT_ID is gone by the time this screen mounts.
    // The slides endpoint sits behind jwt.merchant_id_and_code, and the interceptor
    // reads that header from localStorage — without it the call fails and the hero
    // falls back to the plain gradient. Re-hydrate the same way Order.vue does.
    async restoreMerchantInfo(branchCode) {
      if (localStorage.getItem(localStorageName('MERCHANT_ID'))) return;
      try {
        const response = await OrderService.checkMerchantAndBranch({ branch_code: branchCode });
        if (!response.data.result.isSuccess) return;
        const data = response.data.data;
        localStorage.setItem(localStorageName('MERCHANT_ID'), data.merchant_id ?? 0);
        localStorage.setItem(localStorageName('MERCHANT_BRAND_NAME'), data.merchant_brand_name ?? '');
        this.brandName = data.merchant_brand_name ?? '';
      } catch (e) {
        // leave the banner on its fallback rather than blocking the login screen
      }
    },
    async getLandingBanner() {
      const branchCode = this.branch_code || this.$store.state.branchCode
        || this.$route.query.branch_code || this.$route.params.branch_code || '';
      if (!branchCode) return;
      await this.restoreMerchantInfo(branchCode);
      OrderService.getSlidesLandingPage({
        isMintyApi: true,
        branch_code: branchCode,
        where_showing: 4,
        is_ordering_online: 1
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.slidesLandingPage = response.data.data || [];
          }
        },
        () => {}
      );
    },
    openTerms() {
      this.termsViewOnly = true;
      $('.bottom_modal_terms').click();
    },
    showErrorModal(message) {
      this.modalErrorMessage = message;
      this.$nextTick(() => {
        $('.btn-success-modal').click();
      });
    },
    validatePhone: async function () {
      this.closeSnackbar()
      this.showLoader()
      var merchantId = localStorage.getItem(localStorageName('MERCHANT_ID'), 0);
      var merchantCode = this.$route.params.merchant_code ?? '';

      if (!merchantId && merchantCode != '') {
        await MemberService.getMerchantId({
          merchant_code: merchantCode
        })
        .then(
          (response) => {
            if (response.data.result.isSuccess) {
              merchantId = response.data.data.merchantId;
              axios.defaults.headers.common['MerchantId'] = merchantId;
              localStorage.setItem(localStorageName('MERCHANT_ID'), merchantId);
            } else {
              this.snackbar = 'danger'
              this.message = response.data.result.message
              this.setTimeOutNotification()
              this.hideLoader()
            }
          },
          (error) => {
            this.snackbar = 'danger'
            this.message = error
            this.hideLoader()
          }
        )
      }

      if (merchantId) {
        await AuthService.validatePhoneNumber(this.form1.phone_number, this.form1.phone_code).then(
          (response) => {
            if (response.data.result.isSuccess) {
              if(response.data.data.is_first_login && response.data.data.is_empty_passcode) {
                // first time login
                if (this.onlyAllowExistingMemberMooLogin) {
                  this.showErrorModal(this.$t('signIn.memberNotFound'));
                } else {
                  this.termsViewOnly = false;
                  $(".bottom_modal_terms").click();
                  this.isFirstLogin = true
                  this.form2.is_empty_passcode = response.data.data.is_empty_passcode;
                }
              } else {
                this.isLoginNormal = false;
                this.isFirstLogin = false
                this.form1.i_agree = true
                this.isOtp = false;
                if(!response.data.data.is_empty_passcode) {
                  this.isAlreadyPasscode = true;
                  this.isPasscode = false;
                  this.isConfirmPasscode = false;
                } else {
                  this.isAlreadyPasscode = false;
                  this.isPasscode = true;
                  this.isConfirmPasscode = true;
                }
                this.form2.phone_number = this.form1.phone_number;
                this.form2.is_empty_passcode = response.data.data.is_empty_passcode;
                // this.sendOTP()
              }
            } else {
              this.snackbar = 'danger'
              this.message = response.data.result.message
              this.setTimeOutNotification()
            }
            this.hideLoader()
          },
          (error) => {
            this.snackbar = 'danger'
            this.message = error
            this.hideLoader()
          }
        )
      }
    },
    agree() {
      // Pressing "I agree" is the consent action itself (no separate checkbox)
      this.form1.i_agree = true
      this.sendOTP()
    },
    validatePhoneNumber() {
      this.form1.phone_number = this.form1.phone_number.replaceAll(' ', '');
      this.form1.phone_number = this.form1.phone_number.replaceAll('+65', '');
    },
    sendOTP: function (isResend = false) {
      var _this = this;
      _this.closeSnackbar();
      _this.stopCount();
      _this.$store.dispatch("auth/sendOtp", _this.form1).then(
        (data) => {
          $(".btn-close").click();
          if (data != undefined && !data.result.isSuccess) {
            _this.message = data.result.message;
            _this.snackbar = "danger";
          } else {
            if (isResend == true) {
              _this.message = data.result.message;
            }
            _this.form2.phone_number = _this.form1.phone_number;
            _this.isLoginNormal = false;
            var sending_otp_signup = localStorage.getItem(localStorageName("SENDING_OTP_SIGNUP"), 0);
            if(sending_otp_signup == 1 || sending_otp_signup == '1') {
              if(this.form1.phone_code == '65') {
                _this.isOtp = true;
                _this.countDown = 120;
                _this.startCount();
              } else {
                _this.isPasscode = true;
                _this.isConfirmPasscode = true;
              }
            } else {
              _this.isPasscode = true;
              _this.isConfirmPasscode = true;
            }
          }
          _this.setTimeOutNotification();
        },
        (error) => {
          console.log("ERROR" + error);
        }
      );
    },
    loginOtp: function () {
      var _this = this;
      AuthService.validateOtp({ phone_number: this.form2.phone_number, otp: this.form2.otp }).then(
        (response) => {
          if (response.result.isSuccess) {
            // enter
            _this.isOtp = false;
            _this.isPasscode = true;
            _this.isConfirmPasscode = true;
          } else {
            this.snackbar = 'danger'
            this.message = response.result.message
            this.setTimeOutNotification();
            _this.clearInputOTP();
          }
          this.hideLoader()
        },
        (error) => {
          this.snackbar = 'danger'
          this.message = error
          this.hideLoader()
        }
      )
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    handleOnComplete(value) {
      this.form2.otp = value;
      this.loginOtp();
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    timedCount() {
      if (this.countDown > 0) {
        this.countDown -= 1;
        this.countDownText = `${this.countDown} s`;
        this.timeout = setTimeout(this.timedCount, 1000);
      }
    },
    startCount() {
      if (!this.timer_on) {
        this.timer_on = 1;
        this.timedCount();
      }
    },
    stopCount() {
      clearTimeout(this.timeout);
      this.countDown = 120;
      this.timer_on = 0;
    },
    callback: function (response) {
      console.log("Handle the response", response);
    },
    showSnackbarError() {
      this.snackbar = "danger";
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    handleOnCompletePasscode: function (value) {
      if(value != "") {
        this.form2.passcode = value;
        this.form2.confirmPasscode = "";
        this.form2.phone_code = this.form1.phone_code;
        setTimeout(() => {
          this.clearInputConfirmPasscode();
        }, 500);
      } else {
        this.form2.passcode = "";
        this.form2.confirmPasscode = "";
        setTimeout(() => {
          this.clearInputConfirmPasscode();
        }, 500);
      }
      this.$nextTick(() => {
        this.$refs.inputConfirmPasscode.$el.querySelector('input').focus();
      });
      // this.isPasscode = false;
      // this.isConfirmPasscode = true;
    },
    handleOnCompleteConfirmPasscode: function (value) {
      if(value === this.form2.passcode) {
        this.handleLogin();
      } else {
        this.snackbar = 'danger'
        this.message = this.$t('signIn.passcodesDoNotMatch')
        this.setTimeOutNotification();
        setTimeout(() => {
          this.clearInputConfirmPasscode();
        }, 500);
      }
    },
    handleOnCompleteAlreadyPasscode: function (value) {
      this.form2.passcode = value;
      this.form2.phone_code = this.form1.phone_code;
      this.handleLogin();
    },
    handleLogin() {
      var _this = this;
      _this.closeSnackbar();
      _this.showLoader();
      _this.$store.dispatch("auth/loginOtp", this.form2).then(
        (data) => {
          if (data != undefined && !data.result.isSuccess) {
            this.hideLoader();
            _this.message = data.result.message;
            _this.showSnackbarError();
            setTimeout(() => {
              _this.clearInputEnterPasscode();
            }, 500);
          } else {
            // save passcode using encrypt
            this.encryptData(this.form2.passcode);
            _this.isLoginNormal = false;
            // MTH045: a fresh login re-arms the menu promo popup so it shows
            // exactly once on the next Order-page entry.
            _this.resetMenuPromoPopupGate();
            // MTH045: after a fresh member login, return to the Order/menu page
            // (like advocado) instead of Profile, so the post-login promo popup
            // can greet the user on the menu.
            // M921: unless the branch points logins somewhere other than the menu.
            _this.gotoPageAfterSignIn();
          }
          _this.hideLoader();
        },
        (error) => {
          setTimeout(() => {
            _this.clearInputEnterPasscode();
          }, 500);
          console.log("ERROR" + error);
        }
      );
    },
    handleEnbaleOptionForgotPasscode() {
      const code = this.$router.currentRoute.value.params.merchant_code ?? "";
      axios.get(process.env.VUE_APP_BASE_MINTY_URL + '/member/get-merchant-setting',
        {
          headers: {
            "MerchantCode": code
          }
        }
      )
        .then((response) => {
          var dataSetting = response.data;
          if (dataSetting.result.isSuccess) {
            if(dataSetting.data && dataSetting.data.getWaBalanceByAdminMerchant >= 1) {
              this.isEnableGetOneTimePasscode = false;
            }
            this.isPasscodeReset = false;
            this.isForgetPassword = true;
          }
        },
          (error) => {
            var status = error.response.status
            console.log(status)
          }
        );
    },
    resetPasscodeToBirthday() {
      this.closeSnackbar();
      this.showLoader();
      AuthService.sendResetToInitialPasscode(this.form1.phone_number, this.form1.phone_code).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.isPasscodeReset = true;
          } else {
            this.snackbar = 'danger';
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = 'danger';
          this.message = error;
          this.hideLoader();
        }
      );
    },
    backToPasscodeEntry() {
      this.isPasscodeReset = false;
      this.form2.passcode = "";
      this.isForgetPassword = false;
    },
    backToPhoneEntry() {
      this.closeSnackbar();
      this.isPasscode = false;
      this.isConfirmPasscode = false;
      this.isOtp = false;
      this.isLoginNormal = true;
      this.form2.passcode = "";
      this.form2.confirmPasscode = "";
    },
    handleForgotPassword() {
      this.closeSnackbar()
      this.showLoader()
      var link = window.location.origin+this.merchantCode+"/forget-passcode";
      AuthService.sendLinkForgotPasscode(this.form1.phone_number, link).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.snackbar = 'success';
            this.message = this.$t('signIn.passcodeSentViaPhone');
            this.setTimeOutNotification();
          } else {
            this.snackbar = 'danger'
            this.message = response.data.result.message
            this.setTimeOutNotification()
          }
          this.hideLoader()
        },
        (error) => {
          this.snackbar = 'danger'
          this.message = error
          this.hideLoader()
        }
      )
    },
    encryptData(passcode) {
      const key = process.env.VUE_APP_KEY_ENCRYPT_MEMBER_INFORMATION;
      if (passcode && key) {
          const encryptedData = CryptoJS.AES.encrypt(passcode, key).toString();
          localStorage.setItem(localStorageName('MEMBER_PASSCODE'), encryptedData ?? '');
      }
    },
    async continueAsGuest() {
      if(this.branch_code != "") {
        // MTH080: after logout (Profile.handleLogout wipes localStorage) MERCHANT_ID
        // may still be missing when the guest taps through — every ordering endpoint
        // sits behind jwt.merchant_id_and_code, so the menu would come back empty.
        // Restore it before navigating; restoreMerchantInfo() is a no-op when set.
        await this.restoreMerchantInfo(this.branch_code);
        // MTH045: entering as guest is a fresh session — re-arm the promo popup.
        this.resetMenuPromoPopupGate();
        this.gotoPageAfterGuest();
      }
    },
    // M921: where a shopper goes once they are signed in. Registering runs through this
    // same OTP screen, so one branch setting covers both.
    gotoPageAfterSignIn() {
      const branch = this.readBranchInfo();
      const setting = Number(branch.moo_after_login_page) || AFTER_LOGIN_ORDERING_PAGE;
      let name = 'Order';

      if (setting === AFTER_LOGIN_MEMBER_PAGE) {
        name = 'Profile';
      } else if (setting === AFTER_LOGIN_REWARD_PAGE) {
        name = 'MyReward';
      }

      router.push({ name, params: this.$route.params });
    },

    // Continuing as a guest is not a login, so the after-login setting does not apply —
    // a guest goes to the menu, which is where this screen has always let them through.
    gotoPageAfterGuest() {
      router.push({ name: 'Order', params: this.$route.params });
    },

    readBranchInfo() {
      try {
        return JSON.parse(localStorage.getItem(localStorageName('BRANCH_INFO'))) || {};
      } catch (e) {
        // Missing or half-written entry — the caller's defaults apply.
        return {};
      }
    },

    // MTH045: clear the "already shown this session" gate so the promo popup
    // shows exactly once on the next Order entry for this fresh login/guest.
    resetMenuPromoPopupGate() {
      try {
        sessionStorage.removeItem(localStorageName(MENU_POPUP_SESSION_KEY));
      } catch (e) {
        // sessionStorage unavailable (private mode) — nothing to reset
      }
    },
    gotoResetToInitialPasscode() {
      this.closeSnackbar()
      this.showLoader()
      AuthService.sendResetToInitialPasscode(this.form1.phone_number, this.form1.phone_code).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.isForgetPassword = false;
            this.isShowEnterPasscodeYourBirtday = true;
          } else {
            this.snackbar = 'danger'
            this.message = response.data.result.message
            this.setTimeOutNotification()
          }
          this.hideLoader()
        },
        (error) => {
          this.snackbar = 'danger'
          this.message = error
          this.hideLoader()
        }
      )
    },
    handleGetOneTimePasscode() {
      this.closeSnackbar()
      this.showLoader()
      AuthService.getOneTimePassCode(this.form1.phone_number, this.form1.phone_code).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.isForgetPassword = false;
            this.isShowEnterPasscodeYourBirtday = false;
            this.isShowEnterOneTimePasscode = true;
            this.snackbar = 'success';
            this.message = this.$t('signIn.passcodeSentViaPhone');
            this.setTimeOutNotification();
          } else {
            this.snackbar = 'danger'
            this.message = response.data.result.message
            this.setTimeOutNotification()
          }
          this.hideLoader()
        },
        (error) => {
          this.snackbar = 'danger'
          this.message = error
          this.hideLoader()
        }
      )
    },
    getCountriesForDropdown() {
      this.isLoadData = false;
      this.showLoader();
      MemberService.getCountryDropdown().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.countryData = response.data.data;
            this.isLoadData = true;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },
    closeModal() {
      $(".btn-close").click()
    },
  },
};
</script>
<style scoped>
/* Themed loading overlay for the login flow */
.mlx-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
}
.mlx-loader-spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid;
  border-color: var(--mlx-accent, #f9dd4b) transparent transparent transparent;
  animation: mlx-loader-spin 0.9s linear infinite;
}
@keyframes mlx-loader-spin {
  to {
    transform: rotate(360deg);
  }
}
/* MTH002 — reskinned phone-number entry (theme2) */
.mlx-page {
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.mlx-hero {
  position: relative;
  height: 56vh;
  min-height: 300px;
  background: #000;
  overflow: hidden;
}
.mlx-swiper,
.mlx-swiper .swiper-slide {
  height: 100%;
}
.mlx-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mlx-hero-fallback {
  position: absolute;
  inset: 0;
}
.mlx-hero-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 38%,
    rgba(0, 0, 0, 0) 60%,
    rgba(0, 0, 0, 0.75) 100%
  );
}
.mlx-brand {
  position: absolute;
  top: 44px;
  left: 24px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
}
.mlx-brand-logo {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  background: #fff;
}
.mlx-brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.mlx-brand-name {
  color: #fff;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.02em;
}
/* MTH069 — language pill on the hero (mirrors the menu top bar's selector).
   Sits top-right, optically centred against the 48px brand logo on the left. */
.mlx-lang-wrap {
  position: absolute;
  top: 50px;
  right: 24px;
  /* Above .mlx-card (z-index 6): this element opens a stacking context, so the
     backdrop/menu z-indexes below are relative to *this* value — anything lower
     would leave the click-away backdrop trapped underneath the card. */
  z-index: 12;
  display: inline-flex;
}
.mlx-lang {
  display: inline-flex;
  align-items: stretch;
  height: 36px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.mlx-lang-seg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0 0.75rem;
  border: none;
  background: #201a1a;
  color: #ffffff;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.mlx-lang-seg:active {
  filter: brightness(0.92);
}
.mlx-lang-text {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
}
.mlx-lang-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  padding: 0;
  border: none;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  color: #201a1a;
  cursor: pointer;
}
.mlx-lang-caret:active {
  transform: scale(0.94);
}
.mlx-lang-caret i {
  font-size: 20px;
  color: #201a1a;
}
.mlx-lang-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}
.mlx-lang-menu {
  position: absolute;
  top: calc(100% + 7px);
  right: 0;
  z-index: 41;
  min-width: 132px;
  margin: 0;
  padding: 0.3rem;
  list-style: none;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
}
.mlx-lang-option {
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #201a1a;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;
}
.mlx-lang-option:not(.is-active):active {
  background: rgba(0, 0, 0, 0.05);
}
.mlx-lang-option.is-active {
  color: #ffffff;
}
.mlx-pagination {
  position: absolute;
  bottom: 38px;
  left: 24px;
  right: 24px;
  z-index: 5;
  display: flex;
  gap: 6px;
}
.mlx-pagination :deep(.swiper-pagination-bullet) {
  height: 4px;
  width: 6px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.4);
  opacity: 1;
  transition: width 0.3s ease;
}
.mlx-pagination :deep(.swiper-pagination-bullet-active) {
  width: 24px;
  background: #fff;
}
.mlx-card {
  position: relative;
  z-index: 6;
  margin-top: -24px;
  background: #fff;
  border-radius: 24px 24px 0 0;
  padding: 32px 24px 40px;
  flex: 1;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.05);
}
.mlx-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mlx-accent);
  margin: 0 0 8px 4px;
}
.mlx-input-box {
  display: flex;
  align-items: center;
  border: 1px solid rgba(215, 194, 192, 0.6);
  border-radius: 16px;
  background: #fff;
  transition: border-color 0.2s ease;
}
.mlx-input-box:focus-within {
  border-color: var(--mlx-accent);
}
.mlx-code {
  padding-left: 8px;
  border-right: 1px solid rgba(215, 194, 192, 0.6);
}
.mlx-select {
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 16px;
  color: #201a1a;
  padding: 14px 6px;
  outline: none;
  cursor: pointer;
}
.mlx-code-fixed {
  display: inline-block;
  font-weight: 600;
  font-size: 16px;
  color: #201a1a;
  padding: 14px 10px 14px 6px;
  white-space: nowrap;
}
.mlx-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 18px;
  font-weight: 500;
  color: #201a1a;
  padding: 14px 16px;
  text-align: left;
}
/* MTH054: the broad `input::placeholder` rule further down centres every
   placeholder on this screen (it is still needed for the OTP inputs), so the
   phone placeholder is pinned back to the left here — same as SFQR. */
.mlx-input::placeholder {
  color: rgba(133, 115, 114, 0.5);
  text-align: left;
}
.mlx-btn-primary {
  width: 100%;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  padding: 16px;
  border-radius: 16px;
  margin-top: 24px;
  cursor: pointer;
  transition: transform 0.1s ease, opacity 0.2s ease;
}
.mlx-btn-primary:active {
  transform: scale(0.98);
}
.mlx-btn-pill {
  border-radius: 9999px;
  margin-top: 8px;
}
/* MTH005 — passcode entry card */
.mlx-card-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.mlx-card-center .notifications {
  width: 100%;
}
.mlx-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--mlx-accent);
  margin: 8px 0 10px;
}
.mlx-subtitle {
  font-size: 14px;
  line-height: 1.5;
  color: var(--mlx-accent);
  margin: 0 0 32px;
  max-width: 300px;
}
.mlx-otp-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 32px;
}
:deep(.mlx-otp-box) {
  width: 60px;
  height: 64px;
  margin: 0 8px;
  border: 2px solid rgba(215, 194, 192, 0.7);
  border-radius: 16px;
  background: #fff;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: var(--mlx-accent);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
:deep(.mlx-otp-box:focus) {
  border-color: var(--mlx-accent);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04);
}
.mlx-resend {
  font-size: 13px;
  color: var(--mlx-accent);
  margin-top: 24px;
}
.mlx-resend a {
  color: var(--mlx-accent);
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
}
.mlx-strong {
  display: block;
  margin-top: 4px;
  font-weight: 700;
  color: var(--mlx-accent);
}
.mlx-countdown {
  font-size: 13px;
  color: var(--mlx-accent);
  margin-top: 16px;
}
.mlx-hint {
  font-size: 11px;
  color: var(--mlx-accent);
  opacity: 0.75;
  margin-top: 16px;
}
/* MTH008 — Advocado-style create-PIN card */
.mlx-pin-card {
  text-align: left;
}
.mlx-pin-heading {
  font-size: 24px;
  margin: 4px 0 20px;
}
.mlx-perks {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.mlx-perk {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #faf7f7;
  border-radius: 16px;
  padding: 18px 8px;
  text-align: center;
}
.mlx-perk-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-size: 24px;
  background: rgba(215, 194, 192, 0.3);
  color: var(--mlx-accent);
}
.mlx-perk-icon-fill {
  background: var(--mlx-accent);
  color: #fff;
}
.mlx-perk-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: #201a1a;
}
.mlx-pin-back {
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  color: #201a1a;
  cursor: pointer;
  margin-bottom: 16px;
}
.mlx-pin-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--mlx-accent);
  margin: 0 0 8px;
}
.mlx-pin-sub {
  font-size: 14px;
  line-height: 1.5;
  color: #857372;
  margin: 0 0 24px;
}
.mlx-pin-field {
  margin-bottom: 20px;
}
.mlx-pin-field .mlx-label {
  margin-left: 0;
}
.mlx-pin-otp {
  justify-content: center;
  margin-top: 10px;
  margin-bottom: 0;
}
.mlx-card-center .mlx-btn-guest {
  margin-top: 10px;
}
.mlx-or {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 20px 0;
}
.mlx-line {
  flex: 1;
  height: 1px;
  background: rgba(215, 194, 192, 0.6);
}
.mlx-or-text {
  font-size: 12px;
  font-weight: 700;
  color: #857372;
  text-transform: uppercase;
}
.mlx-btn-guest {
  width: 100%;
  background: transparent;
  border: 2px solid;
  font-weight: 700;
  font-size: 18px;
  padding: 15px;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.1s ease;
}
.mlx-btn-guest:active {
  transform: scale(0.98);
}
.mlx-footer {
  text-align: center;
  font-size: 12px;
  line-height: 1.6;
  color: var(--mlx-accent);
  margin: 28px 8px 0;
}
.mlx-footer a {
  color: var(--mlx-accent);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.otp-input {
  width: 40px;
  height: 40px;
  padding: 5px;
  margin: 0 10px;
  font-size: 20px;
  border: none;
  border-bottom: 2px solid var(--label-color);
  text-align: center;
  border-radius: 0px !important;
  background: none;
}

.form-control:disabled,
.form-control[readonly] {
  padding: 2px !important;
}

.text-green {
  color: #ffbe30;
}

.otp-input:focus {
  outline: none !important;
}

.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input::placeholder {
  font-size: 15px;
  text-align: center;
  font-weight: 600;
}

#phone_number {
  /* MTH054: 14px 16px matches SFQR's phone input (0.875rem 1rem). The !important
     is kept because the old rule used it; without the padding the text sat flush
     against the +65 divider. */
  padding: 14px 16px !important;font-size: 1.1rem;
}

#phone_code {
  padding: 0px !important;font-size: 1.1rem;
  cursor: not-allowed;
  background-image: linear-gradient(0deg, var(--label-color) 2.1px, rgba(156, 39, 176, 0) 0), linear-gradient(0deg, #f0f2f5 1px, rgba(209, 209, 209, 0) 0) !important;
}
</style>
